import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "@/AuthContext";
import { useI18n } from "@/i18n";

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const isGift = sp.get("gift") === "1";
  const { t } = useI18n();
  const [status, setStatus] = useState("polling");
  const [recipeId, setRecipeId] = useState(null);
  const [kind, setKind] = useState(null);
  const [city, setCity] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const maxAttempts = 12;
    const interval = 2500;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setRecipeId(res.data.recipe_id);
        setKind(res.data.kind);
        setCity(res.data.city);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired" || attempts >= maxAttempts) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, interval);
      } catch (_) {
        if (attempts >= maxAttempts) setStatus("failed");
        else timer = setTimeout(poll, interval);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  // Where to send the user next based on what they bought
  const cta = (() => {
    if (isGift) {
      return { to: "/dashboard", label: t("gift_success_cta") || "Done" };
    }
    if (kind === "capital" && city) {
      return { to: `/recipes?city=${encodeURIComponent(city)}`, label: `${t("cta_view")} ${city}` };
    }
    if (kind === "grand_tour") {
      return { to: "/dashboard", label: t("page_dashboard_title") };
    }
    if (recipeId) {
      return { to: `/recipes/${recipeId}`, label: t("success_cta") };
    }
    return { to: "/dashboard", label: t("page_dashboard_title") };
  })();

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="glass p-12 max-w-lg text-center">
        {status === "polling" && (
          <>
            <div className="overline">Stripe</div>
            <h1 className="font-serif text-3xl tracking-tighter mt-2" data-testid="checkout-polling">{t("pending")}</h1>
            <div className="animate-pulse mt-6 text-zinc-500">…</div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="overline">✓ {isGift ? t("gift_sent_overline") : "Paid"}</div>
            <h1 className="font-serif text-4xl tracking-tighter mt-3 italic gold" data-testid="checkout-success-title">
              {isGift ? t("gift_sent_title") : t("success_title")}
            </h1>
            <p className="text-zinc-400 mt-4">
              {isGift ? t("gift_sent_sub") : t("success_sub")}
            </p>
            <Link to={cta.to} className="btn-gold mt-8 inline-flex" data-testid="open-recipe-btn">{cta.label} →</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="overline">Stripe</div>
            <h1 className="font-serif text-3xl tracking-tighter mt-2" data-testid="checkout-failed">{t("fail_title")}</h1>
            <p className="text-zinc-500 mt-3">{t("fail_sub")}</p>
            <Link to="/recipes" className="btn-ghost mt-6 inline-flex">{t("nav_portfolio")}</Link>
          </>
        )}
      </div>
    </main>
  );
}
